import React, { useState, useRef } from 'react';
import { Upload, Youtube, FileText, CheckCircle, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';
import './ResumeUploadRitual.css';

const ResumeUploadRitual = ({ onUploadComplete, onSkip, onUploadStart, onUploadError }) => {
  const { user } = useAuth();
  const [resumeFile, setResumeFile] = useState(null);
  const [youtubeFile, setYoutubeFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState({ resume: 'idle', youtube: 'idle' }); // idle, uploading, success, error
  const [errorMessages, setErrorMessages] = useState({ resume: null, youtube: null });

  const [pollAnswer, setPollAnswer] = useState(null); // 'yes' | 'a_bit' | 'no'
  const [pollSaved, setPollSaved] = useState(false);

  const resumeInputRef = useRef(null);
  const youtubeInputRef = useRef(null);

  const handleFileSelect = (e, type) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      if (type === 'resume') setResumeFile(files[0]);
      if (type === 'youtube') setYoutubeFile(files[0]);
    }
  };

  const handleDrop = (e, type) => {
    e.preventDefault();
    e.stopPropagation();
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      if (type === 'resume') setResumeFile(files[0]);
      if (type === 'youtube') setYoutubeFile(files[0]);
    }
  };

  const handlePollSelect = async (answer) => {
    setPollAnswer(answer);
    // Save to Supabase
    if (user) {
      try {
        await supabase.from('youtube_history_poll').insert({
          user_id: user.id,
          user_email: user.email || null,
          response: answer,
        });
        setPollSaved(true);
      } catch (error) {
        console.error('Failed to save poll response:', error);
      }
    }
  };

  const handleProcessFiles = async () => {
    if (!resumeFile) return;

    setIsUploading(true);
    if (onUploadStart) onUploadStart();

    const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:8000';

    // CRITICAL: Use authenticated user ID, not a random guest ID
    // This ensures resume data is linked to the correct user account
    const userId = user?.id || localStorage.getItem('user_id') || `guest_${Date.now()}`;

    // Get auth token for authenticated requests
    let authToken = null;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      authToken = session?.access_token;
    } catch (e) {
      console.warn("Could not get auth token:", e);
    }

    try {
      // 1. Upload Resume (Real)
      setUploadStatus(prev => ({ ...prev, resume: 'uploading' }));
      const formData = new FormData();
      formData.append('file', resumeFile);


      // 30-second timeout — backend now returns immediately (LLM runs in background)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);

      const headers = {};
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }

      const response = await fetch(`${API_BASE}/api/upload-resume?user_id=${encodeURIComponent(userId)}`, {
        method: 'POST',
        body: formData,
        headers,
        signal: controller.signal
      }).catch(err => {
        clearTimeout(timeoutId);
        console.error('Fetch error:', err);
        if (err.name === 'AbortError') {
          throw new Error('Upload timed out. Make sure the backend is running at ' + API_BASE + ' and try again.');
        }
        throw new Error(`Connection failed: ${err.message}. Make sure the backend is running at ${API_BASE}`);
      });
      
      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = 'Resume upload failed';
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.detail || errorJson.message || errorMessage;
        } catch (e) {
          errorMessage = errorText || `Server returned ${response.status}: ${response.statusText}`;
        }
        console.error('Upload failed:', {
          status: response.status,
          statusText: response.statusText,
          error: errorMessage,
          url: `${API_BASE}/api/upload-resume`
        });
        throw new Error(errorMessage);
      }
      const data = await response.json();
      setUploadStatus(prev => ({ ...prev, resume: 'success' }));
      // localStorage.setItem('resumeData', JSON.stringify(data));

      // 2. Upload YouTube History (non-blocking — failure won't stop onboarding)
      let youtubeData = null;
      if (youtubeFile) {
        setUploadStatus(prev => ({ ...prev, youtube: 'uploading' }));
        try {
            const ytFormData = new FormData();
            ytFormData.append('file', youtubeFile);

            const ytHeaders = {};
            if (authToken) {
              ytHeaders['Authorization'] = `Bearer ${authToken}`;
            }

            // 90-second timeout for YouTube (large PDF files)
            const ytController = new AbortController();
            const ytTimeoutId = setTimeout(() => ytController.abort(), 90000);

            const ytResponse = await fetch(`${API_BASE}/api/upload-youtube-history?user_id=${encodeURIComponent(userId)}`, {
                method: 'POST',
                body: ytFormData,
                headers: ytHeaders,
                signal: ytController.signal,
            }).catch(err => {
                clearTimeout(ytTimeoutId);
                throw err;
            });
            clearTimeout(ytTimeoutId);

            if (ytResponse.ok) {
                youtubeData = await ytResponse.json();
                setUploadStatus(prev => ({ ...prev, youtube: 'success' }));

                if (authToken && youtubeData) {
                  try {
                    await fetch(`${API_BASE}/api/save-youtube-analysis?user_id=${encodeURIComponent(userId)}`, {
                      method: 'POST',
                      headers: {
                        'Authorization': `Bearer ${authToken}`,
                        'Content-Type': 'application/json'
                      },
                      body: JSON.stringify(youtubeData)
                    });
                  } catch (saveErr) {
                    console.warn("Failed to persist YouTube data:", saveErr);
                  }
                }
            } else {
                const errorText = await ytResponse.text().catch(() => '');
                let errorMessage = 'YouTube upload failed';
                try {
                    const errorJson = JSON.parse(errorText);
                    errorMessage = errorJson.detail || errorJson.message || errorMessage;
                } catch (e) {
                    errorMessage = errorText || `Server returned ${ytResponse.status}`;
                }
                console.warn("YouTube upload failed (non-blocking):", errorMessage);
                setUploadStatus(prev => ({ ...prev, youtube: 'error' }));
                setErrorMessages(prev => ({ ...prev, youtube: errorMessage }));
                // Non-blocking: show inline error, don't alert
            }
        } catch (e) {
            const errorMessage = e.name === 'AbortError' ? 'YouTube upload timed out' : (e.message || 'Failed to upload YouTube history');
            console.warn("YouTube upload error (non-blocking):", errorMessage);
            setUploadStatus(prev => ({ ...prev, youtube: 'error' }));
            setErrorMessages(prev => ({ ...prev, youtube: errorMessage }));
            // Non-blocking: continue to onUploadComplete regardless
        }
      }

      // Complete
      if (onUploadComplete) {
        onUploadComplete({
            ...data,
            youtube: youtubeData
        });
      }

    } catch (error) {
      console.error('Upload Error:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        API_BASE: API_BASE,
        userId: userId,
        fileName: resumeFile?.name,
        fileSize: resumeFile?.size
      });

      setUploadStatus(prev => ({ ...prev, resume: 'error' }));
      setIsUploading(false);
      if (onUploadError) onUploadError(error);

      // Report error to backend for real-time monitoring
      try {
        await fetch(`${API_BASE}/api/debug/log-error`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            endpoint: 'upload-resume-client',
            error: error.message,
            user_agent: navigator.userAgent,
            url: window.location.href,
            extra: { fileName: resumeFile?.name, fileSize: resumeFile?.size }
          })
        }).catch(() => {}); // Ignore if this fails too
      } catch (e) {}

      // More informative error message
      let errorMsg = "Failed to upload resume. Please try again.";
      if (error.message) {
        if (error.message.includes('timed out')) {
          errorMsg = error.message;
        } else if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError') || error.message.includes('aborted')) {
          errorMsg = `Cannot connect to server. Make sure the backend is running at ${API_BASE}`;
        } else if (error.message.includes('CORS')) {
          errorMsg = "Connection blocked. Please try again or contact support.";
        } else {
          errorMsg = `Upload failed: ${error.message}`;
        }
      }
      alert(errorMsg);
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto p-6 animate-in slide-in-from-bottom-4 duration-700">

      <div className="mb-10 text-center">
         <div className="max-w-2xl mx-auto bg-black rounded-xl overflow-hidden shadow-2xl border border-slate-200 ring-4 ring-slate-50">
             <video controls className="w-full h-auto" playsInline>
                 <source src="/YoutubeTutorial.MOV" type="video/mp4" />
                 <source src="/YoutubeTutorial.MOV" type="video/quicktime" />
                 Your browser does not support the video tag.
             </video>
         </div>
         <a
           href="https://www.youtube.com/feed/history"
           target="_blank"
           rel="noopener noreferrer"
           className="inline-flex items-center gap-2 mt-4 text-base font-medium text-blue-600 hover:text-blue-800 hover:underline"
         >
           <Youtube size={18} />
           open your youtube watch history →
         </a>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* RESUME BOX */}
        <div 
            className={`border-2 border-dashed rounded-xl p-8 flex flex-col items-center justify-center text-center transition-all cursor-pointer bg-white
                ${resumeFile ? 'border-green-500 bg-green-50' : 'border-slate-200 hover:border-blue-400 hover:bg-slate-50'}`}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => handleDrop(e, 'resume')}
            onClick={() => resumeInputRef.current?.click()}
        >
            <input ref={resumeInputRef} type="file" accept=".pdf,.docx,.txt" className="hidden" onChange={(e) => handleFileSelect(e, 'resume')} />
            {resumeFile ? (
                <>
                    <FileText size={48} className="text-green-600 mb-4" />
                    <p className="font-semibold text-green-700 truncate max-w-full">{resumeFile.name}</p>
                    <p className="text-xs text-green-600 mt-1">Ready to upload</p>
                </>
            ) : (
                <>
                    <Upload size={48} className="text-slate-300 mb-4" />
                    <p className="font-semibold text-slate-700">Upload Resume PDF</p>
                    <p className="text-xs text-slate-400 mt-1">PDF preferred (DOCX, TXT also accepted)</p>
                    <p className="text-xs text-slate-400 mt-3 italic">To protect your identity, feel free to upload a resume with your first name only and no address.</p>
                </>
            )}
        </div>

        {/* YOUTUBE BOX */}
        <div
            className={`border-2 border-dashed rounded-xl p-8 flex flex-col items-center justify-center text-center transition-all cursor-pointer bg-white
                ${youtubeFile ? 'border-green-500 bg-green-50' : 'border-slate-200 hover:border-green-300 hover:bg-slate-50'}`}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => handleDrop(e, 'youtube')}
            onClick={() => youtubeInputRef.current?.click()}
        >
            <input ref={youtubeInputRef} type="file" accept=".pdf,.zip" className="hidden" onChange={(e) => handleFileSelect(e, 'youtube')} />
            {youtubeFile ? (
                <>
                    <Youtube size={48} className="text-green-600 mb-4" />
                    <p className="font-semibold text-green-700 truncate max-w-full">{youtubeFile.name}</p>
                    <p className="text-xs text-green-600 mt-1">Ready to analyze</p>
                </>
            ) : (
                <>
                    <Youtube size={48} className="text-slate-300 mb-4" />
                    <p className="font-semibold text-slate-700">YouTube History</p>
                    <p className="text-xs text-slate-400 mt-1">Upload PDF or ZIP export</p>
                </>
            )}
        </div>
      </div>

      {/* YouTube Career Interest Poll */}
      <div className="mt-8 max-w-md mx-auto">
        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 text-center">
          <p className="text-sm font-semibold text-slate-700 mb-3">
            In your opinion, does your YouTube watch history represent any career interests?
          </p>
          <div className="flex items-center justify-center gap-3">
            {[
              { label: 'Yes', value: 'yes' },
              { label: 'A bit', value: 'a_bit' },
              { label: 'No', value: 'no' },
            ].map((option) => (
              <button
                key={option.value}
                onClick={() => handlePollSelect(option.value)}
                disabled={pollSaved}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition-all
                  ${pollAnswer === option.value
                    ? 'bg-black text-white scale-105 shadow-md'
                    : 'bg-white text-slate-600 border border-slate-200 hover:border-slate-400 hover:bg-slate-100'
                  }
                  ${pollSaved && pollAnswer !== option.value ? 'opacity-40 cursor-default' : ''}
                  ${pollSaved ? 'cursor-default' : 'cursor-pointer'}`}
              >
                {option.label}
              </button>
            ))}
          </div>
          {pollSaved && (
            <p className="text-xs text-green-600 mt-2 font-medium">Thanks for sharing!</p>
          )}
        </div>
      </div>

      <div className="mt-6 flex justify-center">
        <button
            onClick={handleProcessFiles}
            disabled={!resumeFile || isUploading}
            className={`flex items-center gap-3 px-8 py-4 rounded-full font-bold text-lg shadow-xl transition-all
                ${!resumeFile || isUploading 
                    ? 'bg-slate-200 text-slate-400 cursor-not-allowed' 
                    : 'bg-black text-white hover:bg-slate-800 hover:scale-105'}`}
        >
            {isUploading ? (
                <>Using AI to analyze...</>
            ) : (
                <>Psychoanalyze Me</>
            )}
        </button>
      </div>

      {isUploading && (
          <div className="mt-6 space-y-2 max-w-md mx-auto">
              <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2">
                       {uploadStatus.resume === 'success' ? <CheckCircle size={16} className="text-green-500"/> : <div className="w-4 h-4 rounded-full border-2 border-slate-200 border-t-blue-500 animate-spin"/>}
                       Parsing Resume...
                  </span>
              </div>
              {youtubeFile && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2">
                        {uploadStatus.youtube === 'success' ? <CheckCircle size={16} className="text-green-500"/> :
                         uploadStatus.youtube === 'error' ? <AlertCircle size={16} className="text-red-500"/> :
                         uploadStatus.resume === 'success' ? <div className="w-4 h-4 rounded-full border-2 border-slate-200 border-t-green-500 animate-spin"/> : <div className="w-4 h-4 rounded-full border-2 border-slate-100"/>}
                        {uploadStatus.youtube === 'error' ? 'YouTube upload failed' : 'Extracting key interests from YouTube...'}
                    </span>
                  </div>
              )}
              {errorMessages.youtube && (
                  <div className="mt-2 text-xs text-red-600 bg-red-50 p-2 rounded">
                      {errorMessages.youtube}
                  </div>
              )}
          </div>
      )}
    </div>
  );
};

export default ResumeUploadRitual;

